import * as PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {LEGACY_WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import LegacyDropdownMenu from 'ui-library/dropdowns/LegacyDropdownMenu';
import * as modals from 'util/Modals';
import {STAGE_RUN_STATUS} from 'workflows/constants/StageRunConstants';
import * as StageRunActions from 'workflows/StageRunActions';
import {STAGE_RUN_FULL_SHAPE, StageRun} from 'workflows/WorkflowConstants';

interface StageRunMenuProps {
  onEditStageRun: () => void;
  onOpenSetExperimentalCondition: () => void;
  openSetExperimentalConditionDisabled: boolean;
  onCopySelectedPath: () => void;
  stageRun: StageRun;
  isStageEditable: boolean;
  isWorkflowEditable: boolean;
}

class StageRunMenu extends React.Component<StageRunMenuProps> {
  static propTypes = {
    onEditStageRun: PropTypes.func.isRequired,
    onOpenSetExperimentalCondition: PropTypes.func.isRequired,
    openSetExperimentalConditionDisabled: PropTypes.bool.isRequired,
    onCopySelectedPath: PropTypes.func.isRequired,
    stageRun: STAGE_RUN_FULL_SHAPE.isRequired,
    isStageEditable: PropTypes.bool.isRequired,
    isWorkflowEditable: PropTypes.bool.isRequired,
  };

  _updateStageRunStatus(status: STAGE_RUN_STATUS): void {
    StageRunActions.bulkUpdateStageRunStatus({
      updateStatusSpecifiers: [
        {
          stageRunId: this.props.stageRun.id,
          status,
        },
      ],
    });
  }

  _handleSelect(status: STAGE_RUN_STATUS): void {
    if (status === STAGE_RUN_STATUS.DISCARDED) {
      modals
        .confirm(
          'Confirm discard stage run',
          `Discard the ${this.props.stageRun.name} stage run and delete all downstream stage runs? \
           This action is not reversible.`,
          'danger',
          {acceptText: 'Discard', rejectText: 'Cancel'}
        )
        .done(() => {
          this._updateStageRunStatus(status);
        });
    } else {
      this._updateStageRunStatus(status);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleClickDeleteStageRun = () => {
    StageRunActions.deleteStageRun(this.props.stageRun, {shouldDeleteDownstreamRuns: false});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleClickDeleteStageRunAndDownstreamRuns = () => {
    StageRunActions.deleteStageRun(this.props.stageRun, {shouldDeleteDownstreamRuns: true});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleRerunStageRun = () => {
    StageRunActions.rerunStageRun(this.props.stageRun.id);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleDuplicateBranch = () => {
    this.props.onCopySelectedPath();
    analytics.deprecatedTrackEvent(LEGACY_WORKFLOWS_ANALYTICS_ACTIONS.DUPLICATED_BRANCH);
  };

  render(): JSX.Element {
    const {
      onEditStageRun,
      onOpenSetExperimentalCondition,
      stageRun,
      isStageEditable,
      isWorkflowEditable,
      openSetExperimentalConditionDisabled,
    } = this.props;
    const isStageRunEditable = stageRun.stageEntry.permissions.writable;
    const menuSections = _.compact([
      isStageRunEditable && [
        {
          name: 'Update status',
          submenu: [
            [
              {
                name: 'Initialized',
                handleOnClick: () => {
                  this._handleSelect(STAGE_RUN_STATUS.INITIALIZED);
                },
              },
              {
                name: 'Completed',
                handleOnClick: () => {
                  this._handleSelect(STAGE_RUN_STATUS.COMPLETED);
                },
              },
              {
                name: 'Discarded',
                handleOnClick: () => {
                  this._handleSelect(STAGE_RUN_STATUS.DISCARDED);
                },
              },
            ],
          ],
        },
      ],
      _.compact([
        isStageRunEditable && {
          name: 'Edit run',
          handleOnClick: onEditStageRun,
        },
        isStageRunEditable && {
          name: 'Set experimental conditions',
          handleOnClick: onOpenSetExperimentalCondition,
          disabled: openSetExperimentalConditionDisabled,
        },
        isStageEditable && {
          name: 'Delete run',
          handleOnClick: this._handleClickDeleteStageRun,
        },
        isWorkflowEditable && {
          name: 'Delete this and all downstream runs',
          handleOnClick: this._handleClickDeleteStageRunAndDownstreamRuns,
        },
      ]),
      _.compact([
        isStageEditable && {
          name: 'Repeat run',
          handleOnClick: this._handleRerunStageRun,
        },
        isWorkflowEditable && {
          name: 'Duplicate branch',
          handleOnClick: this._handleDuplicateBranch,
        },
      ]),
    ]);

    return <LegacyDropdownMenu items={menuSections} menuStyle={LegacyDropdownMenu.STYLES.COMPACT} />;
  }
}

export default StageRunMenu;
